import {
    Article,
    ArticleBodySegmentType,
    ArticlePreview,
    ArticleStatus,
    BodySegment,
    RawHtmlSegment,
} from '@core/models/news/Article';
import { logger } from '@src/app/logger';
import { slugify } from '@src/app/utils/slugify';

import {
    ArticleDto,
    Body as ArticleBodySegmentDto,
    BodyNodeType,
    GenericLinkDto,
    HTMLEmbedType,
} from './dtos/ArticleDto';
import {
    WidgetDto,
    WidgetType,
} from './dtos/WidgetDto';

export function transformArticlePreview(dto: ArticleDto): ArticlePreview {
    return {
        id: dto.id,
        title: dto.title,
        subtitle: dto.subtitle || '',
        status: dto.status as ArticleStatus,
        image: dto.image?.data?.urls?.uploaded?.original,
        datePublished: new Date(dto.published_at),
        datePublishedUntil: dto.published_until ? new Date(dto.published_until) : undefined,
        slug: dto.seo?.slug ?? slugify(dto.title),
        superbetMatchIds: getSuperbetMatchIds(dto),
        ...transformLinkedSuperbetEntities(dto.generic),
    };
}

export function transformFullArticle(dto: ArticleDto): Article {
    const body = transformArticleBody(dto.body);

    return {
        ...transformArticlePreview(dto),
        thumbnailImage: dto.image?.data?.urls?.uploaded?.thumbnail,
        fullImage: dto.image?.data?.urls?.uploaded?.gallery,
        category: dto.category.title,
        seo: dto.seo,
        body,
    };
}

function getSuperbetMatchIds(dto: ArticleDto): number[] {
    const dtoBodyContent: ArticleBodySegmentDto[] = dto.body?.filter((content) => content.data?.content?.includes('superbetMatchId'));
    const superbetMatchIds: number[] = [];

    dtoBodyContent.forEach((item) => {
        const content = JSON.parse(item.data.content);
        if (content.superbetMatchId) {
            superbetMatchIds.push(+content.superbetMatchId);
        }
    });

    return superbetMatchIds;
}

function transformLinkedSuperbetEntities(dto: GenericLinkDto | undefined): LinkedIds | undefined {
    if (!dto) {
        return;
    }

    try {
        return {
            matchId: dto.superbetMatchId ? parseInt(dto.superbetMatchId, 10) : undefined,
            tournamentId: dto.superbetTournamentId ? parseInt(dto.superbetTournamentId, 10) : undefined,
            socialUserId: dto.socialUserID,
        };
    } catch {}
}

function transformArticleBody(nodes: ArticleDto['body']): Article['body'] {
    return nodes.map((node): BodySegment | null => {
        switch (node.type) {
            case BodyNodeType.EditorBlock:
                return transformToRawHtml(node);
            case BodyNodeType.Image:
                return transformImageNodeToHtmlContent(node);
            case BodyNodeType.Embed:
                return transformEmbedNode(node);
            default:
                logger.warn(`Unknown article node type "${node.type}", falling back to HTML`);
                return transformToRawHtml(node);
        }
    }).filter((node) => node != null) as RawHtmlSegment[];
}

function transformToRawHtml(node: ArticleBodySegmentDto): RawHtmlSegment {
    const customClassName = node.data.embed_type === HTMLEmbedType.Video
        ? 'video-iframe'
        : undefined;

    return {
        customClassName,
        content: node.data.content,
        type: ArticleBodySegmentType.RawHtml,
    };
}

function transformImageNodeToHtmlContent(node: ArticleBodySegmentDto): RawHtmlSegment | null {
    // max defensive mode
    const imageUrl = node?.data?.preview?.imageBlock?.image?.urls?.uploaded?.original;
    const imageDescription = node?.data?.description || '';

    return imageUrl != null ? {
        content: `<img src="${imageUrl}" alt="${imageDescription}" />`,
        type: ArticleBodySegmentType.RawHtml,
    } : null;
}

function transformEmbedNode(node: ArticleBodySegmentDto): BodySegment | null {
    try {
        const widgetData = JSON.parse(node.data?.content) as WidgetDto;

        switch (widgetData.type) {
            case WidgetType.Market:
                return {
                    type: ArticleBodySegmentType.MarketOfferWidget,
                    marketId: parseInt(widgetData.superbetMarketId, 10),
                    matchId: parseInt(widgetData.superbetMatchId, 10),
                };
            case WidgetType.Match:
                return {
                    type: ArticleBodySegmentType.MatchOfferWidget,
                    matchId: parseInt(widgetData.superbetMatchId, 10),
                };
            case WidgetType.Superbets:
                return {
                    type: ArticleBodySegmentType.SuperBetsWidget,
                    matchId: parseInt(widgetData.superbetMatchId, 10),
                };
            default:
                return null;
        }
    } catch {
        // We've failed to actually parse it - it's probably an embeded iframe
        // or another textual embed, since only widgets are defined in JSON
        return node.data.content ? transformToRawHtml(node) : null;
    }
}

type LinkedIds = Pick<Article, 'matchId' | 'tournamentId' | 'socialUserId'>;
